import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { MarkerClusterer } from '@react-google-maps/api';
import { Form, Badge, Button, Collapse } from 'react-bootstrap';
import axios from './utils/axiosConfig';

// 防抖自定義 Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const MapComponent = () => {
  const [currentLocation, setCurrentLocation] = useState({ lat: 23.7147, lng: 120.5390 });
  const [selected, setSelected] = useState(null);
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]); // 儲存所有資料
  const [storeNames, setStoreNames] = useState([]); // 社別選項
  const [selectedStore, setSelectedStore] = useState(''); // 選中的社別
  const [searchQuery, setSearchQuery] = useState(''); // 搜尋查詢
  const [showControls, setShowControls] = useState(false); // 控制面板顯示狀態
  const [loading, setLoading] = useState(true);

  // 取得 Token
  const token = localStorage.getItem('token');
  const debouncedSearch = useDebounce(searchQuery, 300); // 防抖搜尋
  
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
        },
        { enableHighAccuracy: true }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }

    // 同時獲取社別資料和位置資料
    Promise.all([
      axios.get('/locations'),
      axios.get('/stores')
    ])
    .then(([locationsResponse, storesResponse]) => {
      const locationsData = locationsResponse.data;
      setAllLocations(locationsData);
      setLocations(locationsData);
      setStoreNames(storesResponse.data);
      setLoading(false);
    })
    .catch(error => {
      console.error("Error fetching data:", error);
      setLoading(false);
    });
  }, [token]); // 當 token 變更時重新發送請求

  // 過濾後的位置資料（基於搜尋和社別篩選）
  const filteredLocations = useMemo(() => {
    return allLocations.filter(location => {
      const matchesSearch = !debouncedSearch || 
        (location.e_name && location.e_name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (location.g_name && location.g_name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (location.s_ss_name && location.s_ss_name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (location.e_comp_name && location.e_comp_name.toLowerCase().includes(debouncedSearch.toLowerCase()));
      
      const matchesStore = !selectedStore || location.s_ss_name === selectedStore;
      
      return matchesSearch && matchesStore;
    });
  }, [allLocations, debouncedSearch, selectedStore]);

  // 更新地圖顯示的位置
  useEffect(() => {
    setLocations(filteredLocations);
  }, [filteredLocations]);

  // 處理社別選擇
  const handleStoreChange = useCallback((e) => {
    setSelectedStore(e.target.value);
  }, []);

  // 處理搜尋輸入
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // 清除篩選
  const clearFilters = useCallback(() => {
    setSelectedStore('');
    setSearchQuery('');
  }, []);

  useEffect(() => {
    const socket = new WebSocket(`${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`);
  
    socket.onopen = () => {
      console.log('WebSocket connection opened');
    };
  
    socket.onmessage = (event) => {
      console.log('Received:', event.data);
    };
  
    socket.onclose = () => {
      console.log('WebSocket connection closed');
    };
  
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  
    return () => {
      socket.close();
    };
  }, []);
  

  const mapStyles = {
    height: "100vh",
    width: "100%"
  };

  // 浮動控制面板樣式
  const controlPanelStyle = {
    position: 'absolute',
    top: '20px',
    left: '20px',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '8px',
    padding: '15px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    zIndex: 1000,
    minWidth: '300px',
    maxWidth: '400px'
  };

  const toggleButtonStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 1001,
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={{ position: 'relative', height: '100vh', width: '100%' }}>
      {/* 浮動切換按鈕 */}
      <Button 
        variant={showControls ? 'danger' : 'primary'}
        style={toggleButtonStyle}
        onClick={() => setShowControls(!showControls)}
        title={showControls ? '隱藏篩選器' : '顯示篩選器'}
      >
        {showControls ? '✕' : '⚙️'}
      </Button>

      {/* 浮動控制面板 */}
      <Collapse in={showControls}>
        <div style={controlPanelStyle}>
          <h6 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontWeight: 'bold' }}>
            🗺️ 地圖篩選器
          </h6>
          
          {/* 社別選擇 */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>選擇社別:</Form.Label>
            <Form.Select
              size="sm"
              value={selectedStore}
              onChange={handleStoreChange}
              style={{ borderRadius: '6px' }}
            >
              <option value="">全部社別</option>
              {storeNames.map((store) => (
                <option key={store.ss_id} value={store.ss_name}>{store.ss_name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* 搜尋輸入 */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '12px', fontWeight: 'bold' }}>搜尋社友+媒合找尋:</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              placeholder="姓名、職位、社別、公司..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ borderRadius: '6px' }}
            />
          </Form.Group>

          {/* 統計資訊 */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Badge bg="primary" style={{ fontSize: '11px' }}>
              顯示 {locations.length} / {allLocations.length} 位社友
            </Badge>
            {(selectedStore || searchQuery) && (
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={clearFilters}
                style={{ fontSize: '10px' }}
              >
                清除篩選
              </Button>
            )}
          </div>

          {/* 狀態顯示 */}
          {selectedStore && (
            <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
              社別: {selectedStore}
            </div>
          )}
          {debouncedSearch && (
            <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
              搜尋: "{debouncedSearch}"
            </div>
          )}
        </div>
      </Collapse>

      <LoadScript googleMapsApiKey='AIzaSyDgVylSVIknzV17Z48u0gc7grLdHCIFdXc'>
        <GoogleMap
          mapContainerStyle={mapStyles}
          zoom={12}
          center={currentLocation}
        >
        <MarkerClusterer>
          {(clusterer) =>
            locations.map((location, index) => (
              <Marker
                key={location.e_id}
                position={{ 
                  lat: location.m_lat + (index * 0.00000008), // 將經緯度略微偏移
                  lng: location.m_lng
                }}
                clusterer={clusterer} // 保留 MarkerClusterer 的功能
                onClick={() => setSelected(location)}
              />
            ))
          }
        </MarkerClusterer>

        {selected && (
          <InfoWindow
            position={{ lat: selected.m_lat, lng: selected.m_lng }}
            onCloseClick={() => setSelected(null)}
          >
            <div style={{ width: '280px', padding: '15px', textAlign: 'center' }}>
              {/* 社友照片 */}
              {selected.e_photo && (
                <img 
                  src={`https://twrc.li.com.tw/uploads/member/${selected.e_photo}`} 
                  alt={selected.e_name} 
                  style={{ 
                    width: '100px', 
                    height: '120px', 
                    borderRadius: '8px', 
                    marginBottom: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }} 
                />
              )}
              
              {/* 社別資訊 */}
              <div style={{ marginBottom: '8px' }}>
                <Badge bg="primary" style={{ fontSize: '12px' }}>
                  {selected.s_ss_name}
                </Badge>
                {selected.g_name && (
                  <Badge bg="secondary" className="ms-1" style={{ fontSize: '11px' }}>
                    {selected.g_name}
                  </Badge>
                )}
              </div>
              
              {/* 姓名 */}
              <h4 style={{ margin: '10px 0', color: '#2c3e50', fontWeight: 'bold' }}>
                {selected.e_name}
              </h4>
              
              {/* 公司資訊 */}
              {selected.e_comp_name && (
                <div style={{ marginBottom: '10px', fontSize: '14px', color: '#34495e' }}>
                  <strong>{selected.e_comp_name}</strong>
                  {selected.e_comp_position && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                      {selected.e_comp_position}
                    </div>
                  )}
                </div>
              )}
              
              {/* 操作按鈕 */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  href={`https://www.google.com.tw/maps/place/${selected.m_lat},${selected.m_lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🧭 導航
                </Button>
                
                {selected.e_mobile && (
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    href={`tel:${selected.e_mobile}`}
                  >
                    📞 撥號
                  </Button>
                )}
                
                {selected.e_comp_web && (
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    href={selected.e_comp_web.startsWith('http://') || selected.e_comp_web.startsWith('https://') ? selected.e_comp_web : `http://${selected.e_comp_web}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🌐 網站
                  </Button>
                )}
              </div>
              
              {/* 聯絡資訊 */}
              {selected.e_mobile && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#7f8c8d' }}>
                  📱 {selected.e_mobile}
                </div>
              )}
            </div>
          </InfoWindow>
        )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default MapComponent;
